// =============================================================================
// YDA E-Learning CMS Service
// =============================================================================
// Fetches dynamic error messages from Strapi CMS with locale support
// =============================================================================

const CMS_CONFIG = {
  BASE_URL: process.env.REACT_APP_CMS_BASE_URL || 'https://yda-lms-cms-staging.dev.kacdn.net',
  ENDPOINT: '/api/error-blocks'
};

// Manual override for testing (remove when CMS API is fixed)
// Organized by locale, then by error code
const MANUAL_ERROR_MESSAGES = {
  en: {
    "elearning/app/callback/authmissing": "Authentication Required",
    "elearning/app/callback/invalidlocale": "Invalid Language", 
    "elearning/app/callback/404": "404 Not Found",
    "elearning/app/default": "Access Error",
    // Backend API error codes
    "auth/invalid-code": "Invalid or Expired Authentication Code",
    "auth/missing-code": "Authentication Code Required",
    "wallet/not-found": "Wallet Not Found",
    "wallet/insufficient-funds": "Insufficient Funds"
  },
  ar: {
    "elearning/app/callback/authmissing": "مطلوب المصادقة",
    "elearning/app/callback/invalidlocale": "لغة غير صالحة",
    "elearning/app/callback/404": "404 غير موجود",
    "elearning/app/default": "خطأ في الوصول",
    // Backend API error codes
    "auth/invalid-code": "رمز المصادقة غير صالح أو منتهي الصلاحية",
    "auth/missing-code": "رمز المصادقة مطلوب",
    "wallet/not-found": "المحفظة غير موجودة",
    "wallet/insufficient-funds": "رصيد غير كافي"
  },
  ur: {
    "elearning/app/callback/authmissing": "تصدیق درکار",
    "elearning/app/callback/invalidlocale": "غلط زبان",
    "elearning/app/callback/404": "404 نہیں ملا",
    "elearning/app/default": "رسائی میں خرابی",
    // Backend API error codes
    "auth/invalid-code": "غلط یا ختم شدہ تصدیقی کوڈ",
    "auth/missing-code": "تصدیقی کوڈ درکار",
    "wallet/not-found": "والیٹ نہیں ملا",
    "wallet/insufficient-funds": "ناکافی فنڈز"
  },
  ma: {
    "elearning/app/callback/authmissing": "പ്രാമാണീകരണം ആവശ്യമാണ്",
    "elearning/app/callback/invalidlocale": "അസാധുവായ ഭാഷ",
    "elearning/app/callback/404": "404 കണ്ടെത്തിയില്ല",
    "elearning/app/default": "പ്രവേശന പിശക്",
    // Backend API error codes
    "auth/invalid-code": "അസാധുവായ അല്ലെങ്കിൽ കാലഹരണപ്പെട്ട പ്രാമാണീകരണ കോഡ്",
    "auth/missing-code": "പ്രാമാണീകരണ കോഡ് ആവശ്യമാണ്",
    "wallet/not-found": "വാലറ്റ് കണ്ടെത്തിയില്ല",
    "wallet/insufficient-funds": "അപര്യാപ്തമായ ഫണ്ടുകൾ"
  }
};

export const cmsService = {
  /**
   * Gets error title and description by error code and locale
   * @param {string} errorCode - Error code to look for
   * @param {string} locale - Language locale (en, ar, ur, ma)
   * @returns {Promise<Object>} Error object with title and optional description
   */
  getErrorMessage: async (errorCode = 'elearning/app/default', locale = 'en') => {
    try {
      // Try CMS API first
      const url = `${CMS_CONFIG.BASE_URL}${CMS_CONFIG.ENDPOINT}?filters[code][$eq]=${errorCode}&locale=${locale}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data?.data?.[0]) {
          const entry = data.data[0];
          const title = entry.attributes?.title || entry.title;
          const description = entry.attributes?.description || entry.description;
          
          if (title) {
            const result = { title };
            if (description) {
              result.description = description;
            }
            return result;
          }
        }
      }
    } catch (error) {
      console.error(`CMS: Failed to fetch ${errorCode}:`, error.message);
    }
    
    // Fallback to manual overrides
    const localeMessages = MANUAL_ERROR_MESSAGES[locale];
    if (localeMessages?.[errorCode]) {
      return { title: localeMessages[errorCode] };
    }
    
    // English fallback
    const englishMessages = MANUAL_ERROR_MESSAGES['en'];
    if (englishMessages?.[errorCode]) {
      return { title: englishMessages[errorCode] };
    }
    
    return { title: 'Access Error' };
  },

  /**
   * Test all error message scenarios
   */
  testErrorMessages: async () => {
    const errorCodes = [
      'elearning/app/callback/authmissing',
      'elearning/app/callback/invalidlocale', 
      'elearning/app/callback/404',
      'elearning/app/default'
    ];
    
    for (const locale of ['en', 'ar', 'ur', 'ma']) {
      console.log(`\nTesting locale: ${locale}`);
      for (const errorCode of errorCodes) {
        try {
          const data = await cmsService.getErrorMessage(errorCode, locale);
          console.log(`${errorCode} (${locale}):`, data);
        } catch (error) {
          console.error(`${errorCode} (${locale}): Error - ${error.message}`);
        }
      }
    }
  },

  /**
   * Test CMS configuration
   */
  testCmsConfig: () => ({
    baseUrl: CMS_CONFIG.BASE_URL,
    endpoint: CMS_CONFIG.ENDPOINT,
    envVar: process.env.REACT_APP_CMS_BASE_URL,
    manualOverrides: Object.keys(MANUAL_ERROR_MESSAGES)
  }),
};

export default cmsService;